import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import Button from 'components/Button';
import DjangoMessages from 'components/DjangoMessages';
import DropdownAction from 'components/Dropdown/DropdownAction';
import InputText from 'components/Form/InputText';
import Icon from 'components/Icon';
import Information from 'components/Information';
import Chain from 'components/Layout/Chain';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import Pill from 'components/Pill';
import SectionHeader from 'components/SectionHeader';
import TablePagination from 'components/Table/TableComponents/TablePagination';
import TableStatic from 'components/Table/TableStatic';
import TooltipWrapper from 'components/Tooltip';
import usePaginatedData from 'hooks/usePaginatedData';
import {useBackend} from 'state/backend';
import {useFilterEmployees, useSearch} from './hooks';
import {ActiveTeamMembersFormData} from './types';

/**
 * Displays the buttons for things like
 * Inviting new team members, transfering ownership, and distibute licences.
 */
const SearchAndActions = () => {
    const {t} = useTranslation('Team.ActiveTeamMembers');

    const {clearSearch, searchValue} = useSearch();

    const {
        data: {actionButtons, isOwner, isSfConnected},
    } = useBackend<ActiveTeamMembersFormData>();

    return (
        <div className="mt-2 flex justify-between">
            <Chain className="input-wrapper">
                <InputText
                    classNameInput="border-none"
                    name="search"
                    placeholder={t('search')}
                />
                <Button
                    aria-label={t('clearSearch')}
                    disabled={!searchValue}
                    icon="close"
                    kind="secondary"
                    onClick={() => clearSearch()}
                />
            </Chain>
            <Chain id="actions">
                {!isSfConnected && (
                    <TooltipWrapper
                        message={actionButtons.inviteTeamMembers?.failureReason}
                    >
                        <DropdownAction
                            disabled={!actionButtons.inviteTeamMembers?.allowed}
                            kind="secondary"
                            label={t('invite.inviteTeamMembers')}
                        >
                            <TooltipWrapper
                                message={
                                    actionButtons.inviteOneMember?.failureReason
                                }
                            >
                                <DropdownAction.Link
                                    disabled={
                                        !actionButtons.inviteOneMember?.allowed
                                    }
                                    href="/home/team/create/"
                                >
                                    {t('invite.inviteOneMember')}
                                </DropdownAction.Link>
                            </TooltipWrapper>
                            <TooltipWrapper
                                message={
                                    actionButtons.bulkInvite?.failureReason
                                }
                            >
                                <DropdownAction.Link
                                    disabled={
                                        !actionButtons.bulkInvite?.allowed
                                    }
                                    href="/data/import/"
                                >
                                    {t('invite.bulkInvite')}
                                </DropdownAction.Link>
                            </TooltipWrapper>
                        </DropdownAction>
                    </TooltipWrapper>
                )}
                {isOwner && (
                    <TooltipWrapper
                        message={actionButtons.transferOwnership?.failureReason}
                    >
                        <LinkButton
                            disabled={!actionButtons.transferOwnership?.allowed}
                            href="/team/change-owner/"
                            kind="secondary"
                        >
                            {t('transferOwnership')}
                        </LinkButton>
                    </TooltipWrapper>
                )}
                <TooltipWrapper
                    message={actionButtons.distributeLicenses?.failureReason}
                >
                    <LinkButton
                        disabled={!actionButtons.distributeLicenses?.allowed}
                        href="/licensing/"
                        kind="secondary"
                    >
                        {t('distributeLicenses')}
                    </LinkButton>
                </TooltipWrapper>
            </Chain>
        </div>
    );
};

/**
 *  This is the first table which lists
 *  the active team members.
 */
const TableTeamMembers = () => {
    const {
        data: {mid},
    } = useBackend<ActiveTeamMembersFormData>();
    const {t} = useTranslation('Team.ActiveTeamMembers');

    const matchedEmployees = useFilterEmployees();

    const {paginatedData, ...pagination} = usePaginatedData(
        matchedEmployees,
        10,
    );

    return (
        <>
            <TableStatic className="mt-2 table-fixed">
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column>{t('name')}</TableStatic.Column>
                        <TableStatic.Column>{t('licensed')}</TableStatic.Column>
                        <TableStatic.Column colSpan={3} />
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body>
                    {paginatedData.length === 0 && (
                        <TableStatic.Row>
                            <TableStatic.Cell colSpan={5}>
                                <div className=" flex items-center">
                                    {t('noResults')}
                                </div>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    {paginatedData.map((employee) =>
                        employee == null ? (
                            <TableStatic.Row
                                key={Math.random()}
                                className="bg-grey-100"
                            >
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                            </TableStatic.Row>
                        ) : (
                            <TableStatic.Row key={employee.mid}>
                                <TableStatic.Cell>
                                    <div className="flex items-center">
                                        <a href={`/home/team/${employee.mid}`}>
                                            {employee.name}
                                        </a>
                                        {employee.mid === mid && (
                                            <Pill
                                                className="ml-2"
                                                color="grey"
                                                label={t('you')}
                                            />
                                        )}
                                    </div>
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    {employee.hasSeat && (
                                        <Icon icon="clients" size="base" />
                                    )}
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    {employee.state === 'invited' && (
                                        <Pill
                                            color="grey"
                                            label={t('invited')}
                                        />
                                    )}
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    {employee.isOwner && (
                                        <Pill
                                            color="grey"
                                            label={t('ownerAdmin')}
                                        />
                                    )}
                                    {!employee.isOwner && employee.isAdmin && (
                                        <Pill color="grey" label={t('admin')} />
                                    )}
                                </TableStatic.Cell>
                                <TableStatic.Cell className="float-right">
                                    <TooltipWrapper
                                        message={
                                            employee.permissions.canViewRights
                                                ?.failureReason
                                        }
                                    >
                                        <LinkButton
                                            disabled={
                                                !employee.permissions
                                                    .canViewRights?.allowed
                                            }
                                            href={`/team/${employee.mid}/rights`}
                                            kind="secondary"
                                            size="xs"
                                        >
                                            {t('viewRights')}
                                        </LinkButton>
                                    </TooltipWrapper>
                                </TableStatic.Cell>
                            </TableStatic.Row>
                        ),
                    )}
                </TableStatic.Body>
            </TableStatic>
            {pagination.pageStop > 1 && (
                <TablePagination className="mt-4" {...pagination} />
            )}
        </>
    );
};

/**
 * This is the second table which lists
 * the API team members.
 */
const TableApiTeamMembers = () => {
    const {t} = useTranslation('Team.ActiveTeamMembers');

    const {data} = useBackend<ActiveTeamMembersFormData>();

    if (data.apiEmployees.length === 0) return null;

    return (
        <>
            <SectionHeader
                className="py-2"
                level={2}
                subtitle={
                    <Trans
                        components={[
                            <a
                                key={0}
                                href="/integrations/makeleaps/api-users/"
                            >
                                {' '}
                            </a>,
                        ]}
                        i18nKey="rightsForMakeLeapsApi"
                        ns="Team.ActiveTeamMembers"
                        t={t}
                    />
                }
                title={t('apiApplications')}
            />
            <TableStatic className="mt-2">
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column colSpan={2}>
                            {t('apiApplication')}
                        </TableStatic.Column>
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body>
                    {data.apiEmployees.map((employee) => (
                        <TableStatic.Row key={employee.mid}>
                            <TableStatic.Cell>
                                <a href={`/home/team/${employee.mid}`}>
                                    {employee.name}
                                </a>
                            </TableStatic.Cell>
                            <TableStatic.Cell className="float-right">
                                <TooltipWrapper
                                    message={
                                        employee.permissions.canViewRights
                                            ?.failureReason
                                    }
                                >
                                    <LinkButton
                                        disabled={
                                            !employee.permissions.canViewRights
                                                ?.allowed
                                        }
                                        href={`/team/${employee.mid}/rights`}
                                        kind="secondary"
                                        size="xs"
                                    >
                                        {t('viewRights')}
                                    </LinkButton>
                                </TooltipWrapper>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    ))}
                </TableStatic.Body>
            </TableStatic>
        </>
    );
};

const LimitUsageNotification = () => {
    const {
        data: {
            activeUserLimit,
            billingStatusIsAtLimit,
            partnerHasStandard,
            planName,
        },
    } = useBackend<ActiveTeamMembersFormData>();

    const {t} = useTranslation('Team.ActiveTeamMembers');

    if (!partnerHasStandard) return null;

    if (activeUserLimit) {
        if (billingStatusIsAtLimit) {
            return (
                <Information className="my-4" type="system">
                    <Trans
                        components={[
                            <a key={0} href="/billing/prices/">
                                {' '}
                            </a>,
                        ]}
                        i18nKey="usageLimitMessage"
                        t={t}
                        values={{activeUserLimit}}
                    />
                </Information>
            );
        }

        return <p>{t('usageLimitUnlimited', {planName})}</p>;
    }

    return null;
};

/**
 * Display Email Verification Flagged Banner.
 * TODO: 2024-03-29 - Check if this flag is still required
 */
const DisplayEmailVerificationFlaggedBanner = () => {
    const {checkFlag, data} = useBackend<ActiveTeamMembersFormData>();
    const {t} = useTranslation('Team.ActiveTeamMembers');

    if (!checkFlag('enforce_email_verification')) return null;

    if (data.isAdmin && !data.primaryEmailVerified) {
        return (
            <Information icon="danger" type="danger">
                {t('emailVerificationBanner')}
                <div className="mt-4 flex justify-end gap-4">
                    <LinkButton
                        href={window.Urls.email_settings_view()}
                        kind="secondary"
                        size="xs"
                    >
                        {t('emailVerificationBannerButton')}
                    </LinkButton>
                </div>
            </Information>
        );
    }

    return null;
};

const ActiveTeamMembers = () => {
    const {
        data: {navigationTabs},
    } = useBackend<ActiveTeamMembersFormData>();

    const methods = useForm();

    const {t} = useTranslation('Team.ActiveTeamMembers');

    return (
        <div>
            <DjangoMessages />
            <FormProvider {...methods}>
                <NavigationTabs.Team
                    className="my-4"
                    selection="members"
                    {...navigationTabs}
                />
                <DisplayEmailVerificationFlaggedBanner />
                <SectionHeader title={t('activeTeamMembers')} />
                <LimitUsageNotification />
                <SearchAndActions />
                <TableTeamMembers />
                <TableApiTeamMembers />
            </FormProvider>
        </div>
    );
};

export default ActiveTeamMembers;
